/**
 * SigningStepMonitor - Invisible per-step signing monitor (multi-payload wizards).
 *
 * Renders nothing; drives one signing step to completion and fires callbacks.
 * Provider-aware: Xaman steps resolve via the WS/polling hook (fetching the txid
 * from REST when the WS omits it); client-signing (joey-wc) steps resolve via the
 * WalletConnect hook, which signs over the session and verifies on-chain.
 *
 * Hooks can't branch at runtime, so the exported component mounts exactly one of
 * two inner monitors based on payload.provider.
 */

import { useRef, useCallback } from 'react';
import { useSigningRequest } from '../../hooks/useSigningRequest';
import { useWcSigningRequest } from '../../hooks/useWcSigningRequest';
import { getWalletProvider } from '../../lib/wallet';
import { apiClient } from '../../api/client';
import type { SigningPayload } from '../../types/signing';

interface SigningStepMonitorProps {
  payload: SigningPayload;
  onSigned: (txHash: string) => void;
  onRejected: () => void;
  /** message distinguishes a real timeout / soft "broadcast-but-unconfirmed" notice. */
  onExpired: (message?: string) => void;
  onError: (msg: string) => void;
}

/**
 * Fetch txid from Xaman REST API with retries.
 * Xaman WS often sends {signed: true} without a txid.
 */
async function fetchTxid(payloadUuid: string): Promise<string | null> {
  for (let attempt = 0; attempt < 10; attempt++) {
    try {
      const response = await apiClient.get<any>(`/offers/payload/${payloadUuid}`);
      if (response.success && response.data?.txid) {
        return response.data.txid;
      }
      if (response.data?.expired) return null;
    } catch { /* retry */ }
    await new Promise((r) => setTimeout(r, 2000));
  }
  return null;
}

function XamanStepMonitor({ payload, onSigned, onRejected, onExpired, onError }: SigningStepMonitorProps) {
  const handledRef = useRef(false);

  const handleSigned = useCallback(async (txid: string | undefined) => {
    if (handledRef.current) return;
    handledRef.current = true;
    let txHash = txid;
    if (!txHash) {
      txHash = (await fetchTxid(payload.id)) ?? undefined;
    }
    if (!txHash) {
      onError('Could not retrieve transaction ID. Please try again.');
      return;
    }
    onSigned(txHash);
  }, [payload.id, onSigned, onError]);

  const handleRejected = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    onRejected();
  }, [onRejected]);

  const handleExpired = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    onExpired();
  }, [onExpired]);

  useSigningRequest({
    payload,
    onSigned: handleSigned,
    onRejected: handleRejected,
    onExpired: handleExpired,
    maxPollingAttempts: 600, // Long polling for parallel signing (up to 10 min)
  });

  return null;
}

function WcStepMonitor({ payload, onSigned, onRejected, onExpired }: SigningStepMonitorProps) {
  const handledRef = useRef(false);

  const handleSigned = useCallback((txHash: string) => {
    if (handledRef.current) return;
    handledRef.current = true;
    onSigned(txHash);
  }, [onSigned]);

  const handleRejected = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    onRejected();
  }, [onRejected]);

  const handleExpired = useCallback((message?: string) => {
    if (handledRef.current) return;
    handledRef.current = true;
    onExpired(message);
  }, [onExpired]);

  // useWcSigningRequest already verifies on-chain and returns the validated hash.
  useWcSigningRequest({
    payload,
    onSigned: handleSigned,
    onRejected: handleRejected,
    onExpired: handleExpired,
  });

  return null;
}

export default function SigningStepMonitor(props: SigningStepMonitorProps) {
  return getWalletProvider(props.payload.provider).capabilities.clientSigning
    ? <WcStepMonitor {...props} />
    : <XamanStepMonitor {...props} />;
}
